import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AutoSub, AutoSubOnDestroy, SimpleModalModule, SvgIconModule, User, UserService } from '@motes-sdk';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
    CreatorTermsAcceptanceContext,
    CreatorTermsFeature,
    CreatorTermsService,
} from '@app/services/creator-terms.service';

@AutoSubOnDestroy()
@Component({
    selector: 'creator-terms-acceptance',
    standalone: true,
    imports: [CommonModule, SimpleModalModule, SvgIconModule],
    templateUrl: './creator-terms-acceptance.component.html',
    styleUrls: ['./creator-terms-acceptance.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CreatorTermsAcceptanceComponent implements OnInit, OnChanges, OnDestroy {
    @Input() feature: CreatorTermsFeature;
    /** Keep checkbox visible after DB acceptance (website publish page). */
    @Input() persistCheckbox: boolean = false;
    /** Modal-only usage (story audio / publish-changes) — never render inline checkbox. */
    @Input() modalOnly: boolean = false;

    /** Avoid taking flex space when only used as a modal host (e.g. in header). */
    @HostBinding('class.modal-only')
    get hostModalOnly(): boolean {
        return this.modalOnly;
    }
    /** Controlled modal visibility (story audio / publish-changes). */
    @Input() showModal: boolean = false;
    @Input() modalTitle: string = '';
    @Input() modalDescription: string = '';
    @Input() context: CreatorTermsAcceptanceContext = {};
    @Input() disableActions: boolean = false;

    @Output() showModalChange = new EventEmitter<boolean>();
    @Output() hasAcceptedChange = new EventEmitter<boolean>();
    @Output() checkedChange = new EventEmitter<boolean>();
    @Output() confirmed = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    public hasAccepted: boolean = false;
    public checked: boolean = false;
    public showFullTerms: boolean = false;
    public isSaving: boolean = false;

    public termsTitle = $localize`Terms`;
    public termsFullText = '';
    public agreeLabel = $localize`I agree`;
    public continueLabel = $localize`Continue`;
    public cancelLabel = $localize`Do not agree`;

    private user: User = null;
    /** Prevents cancel side-effects when closing after a successful confirm. */
    private closedByConfirm = false;
    /** Dedupes cancel from outside-click showChange + cancel button onCancel. */
    private isCancelling = false;

    constructor(
        private creatorTermsService: CreatorTermsService,
        private userService: UserService,
    ) { }

    ngOnInit() {
        this.user = this.userService.currentUser;
        this.refreshTermsCopy();
        this.bindAcceptanceWatch();
    }

    ngOnDestroy() { }

    ngOnChanges(changes: SimpleChanges) {
        if(changes['feature'] && !changes['feature'].firstChange) {
            this.refreshTermsCopy();
            this.bindAcceptanceWatch();
        }
        if(changes['showModal']?.currentValue) {
            this.closedByConfirm = false;
            this.isCancelling = false;
        }
    }

    public get showInlineCheckbox(): boolean {
        if(this.modalOnly) return false;
        if(this.persistCheckbox) return true;
        return !this.hasAccepted;
    }

    public onCheckedToggle() {
        this.checked = !this.checked;
        this.checkedChange.emit(this.checked);
    }

    public openFullTerms(event?: Event) {
        event?.stopPropagation();
        this.showFullTerms = true;
    }

    public closeFullTerms() {
        this.checked = true;
        this.checkedChange.emit(true);
        this.showFullTerms = false;
    }

    public cancelModal() {
        if(this.isCancelling) return;
        this.isCancelling = true;

        if(!this.hasAccepted) {
            this.checked = false;
            this.checkedChange.emit(false);
        }
        this.showFullTerms = false;
        this.showModalChange.emit(false);
        this.cancelled.emit();
    }

    /** Outside click only emits showChange (not onCancel) — treat as decline. */
    public onSimpleModalShowChange(show: boolean) {
        if(show) return;
        if(this.closedByConfirm) {
            this.closedByConfirm = false;
            this.showFullTerms = false;
            return;
        }
        this.onModalCancel();
    }

    public onModalOk() {
        if(this.showModal) {
            void this.confirmModal();
            return;
        }
        this.closeFullTerms();
    }

    public onModalCancel() {
        if(this.showFullTerms && !this.showModal) {
            this.showFullTerms = false;
            return;
        }
        if(!this.showModal) return;
        this.cancelModal();
    }

    public async confirmModal() {
        if(this.showFullTerms) {
            this.closeFullTerms();
            return;
        }
        if(!this.checked || this.isSaving) return;

        const ok = await this.ensureAccepted();
        if(!ok) return;

        this.closedByConfirm = true;
        this.showModalChange.emit(false);
        this.confirmed.emit();
    }

    /**
     * Persist acceptance when checkbox is checked and not yet stored.
     * Used by parents before publish / preview / generate.
     */
    public async ensureAccepted(extraContext: CreatorTermsAcceptanceContext = {}): Promise<boolean> {
        if(!this.checked && !this.hasAccepted) return false;
        if(this.hasAccepted) return true;

        this.isSaving = true;
        try {
            const user = this.userService.currentUser || this.user;
            await this.creatorTermsService.ensureAccepted(this.feature, user, {
                ...this.context,
                ...extraContext,
            });
            // Live watch will sync hasAccepted/checked from RTDB.
            return true;
        } finally {
            this.isSaving = false;
        }
    }

    private refreshTermsCopy() {
        if(!this.feature) return;
        this.termsFullText = this.creatorTermsService.getTermsText(this.feature);
        this.termsTitle = this.feature === 'ttsAudioOwnership'
            ? $localize`Audio Generation Terms`
            : $localize`Content Responsibility Terms`;
    }

    private bindAcceptanceWatch() {
        AutoSub(this).reg['acceptanceSub']?.unsubscribe();
        AutoSub(this).reg['acceptanceSub'] = this.userService.watchCurrentUser().pipe(
            switchMap((user) => {
                this.user = user;
                if(!this.feature || !user?.key) return of(false);
                return this.creatorTermsService.watchHasAccepted(this.feature, user.key);
            }),
        ).subscribe((accepted) => this.applyAccepted(accepted));
    }

    private applyAccepted(accepted: boolean) {
        this.hasAccepted = accepted;
        this.checked = accepted;
        this.hasAcceptedChange.emit(this.hasAccepted);
        this.checkedChange.emit(this.checked);
    }
}
